import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../AuthContext";
import { t, LOCALES } from "../i18n";
import api, { errMsg } from "../api";
import { MessageCircle, Loader2, AlertTriangle, RefreshCw, Inbox, Send, ChevronLeft, Download, Wifi, WifiOff, Zap, FileText, Plus, UserCog, Clock, CheckCircle2, Undo2, Trash2, X as XIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";
import ChannelIcon, { channelLabel } from "../components/ChannelIcon";
import SnoozeDialog from "../components/SnoozeDialog";

const CHANNEL_META = {
    whats_app: { label: "WhatsApp" },
    whatsapp: { label: "WhatsApp" },
    sms: { label: "SMS" },
    email: { label: "E-Mail" },
    facebook_messenger: { label: "Messenger" },
    facebook: { label: "Messenger" },
    instagram: { label: "Instagram" },
    live_chat: { label: "Web Chat" },
    telegram: { label: "Telegram" },
};
const meta = (t) => CHANNEL_META[(t || "").toLowerCase()] || { label: t || "?" };

export default function SuperchatPage() {
    const { lang, user: currentUser } = useAuth();
    const [status, setStatus] = useState(null);
    const [channels, setChannels] = useState([]);
    const [channelFilter, setChannelFilter] = useState("all"); // channel_id or 'all'
    const [statusFilter, setStatusFilter] = useState("open"); // open | snoozed | done
    const [snoozeOpen, setSnoozeOpen] = useState(false);
    const [convs, setConvs] = useState([]);
    const [cursor, setCursor] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [active, setActive] = useState(null);
    const [messages, setMessages] = useState([]);
    const [planNotice, setPlanNotice] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [reply, setReply] = useState("");
    const [sending, setSending] = useState(false);
    const [webhookMeta, setWebhookMeta] = useState(null);
    const [wsLive, setWsLive] = useState(false);
    const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
    const [newConvOpen, setNewConvOpen] = useState(false);
    const [contactEditFor, setContactEditFor] = useState(null);
    const [users, setUsers] = useState([]);
    const [assignments, setAssignments] = useState({});  // conv_id -> assignment doc
    const [deletionRequest, setDeletionRequest] = useState(null);  // pending deletion req for active conv
    const [deleteReasonOpen, setDeleteReasonOpen] = useState(false);
    const [deleteReason, setDeleteReason] = useState("");
    const scrollRef = useRef(null);
    const wsRef = useRef(null);
    const activeRef = useRef(null);
    useEffect(() => { activeRef.current = active; }, [active]);

    useEffect(() => {
        api.get("/superchat/status").then((r) => setStatus(r.data)).catch(() => setStatus({ ok: false, detail: "Error" }));
        api.get("/superchat/channels").then((r) => {
            const list = r.data?.data?.results || r.data?.data?.data || [];
            setChannels(Array.isArray(list) ? list : []);
        }).catch(() => {});
        api.get("/superchat/webhook/status").then((r) => setWebhookMeta(r.data?.meta || null)).catch(() => {});
        api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
        api.get("/superchat/assignments").then((r) => {
            const map = {};
            for (const a of (r.data?.results || [])) map[a.conversation_id] = a;
            setAssignments(map);
        }).catch(() => {});
    }, []);

    // Live WebSocket — only matters if webhook is registered
    useEffect(() => {
        const token = localStorage.getItem("taskflow_token");
        if (!token) return;
        const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
        const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/^https?:/, proto);
        const url = `${base}/api/ws/superchat?token=${encodeURIComponent(token)}`;
        let ws;
        try {
            ws = new WebSocket(url);
        } catch (e) { return; /* silent */ }
        wsRef.current = ws;
        ws.onopen = () => setWsLive(true);
        ws.onclose = () => setWsLive(false);
        ws.onerror = () => setWsLive(false);
        ws.onmessage = (ev) => {
            try {
                const data = JSON.parse(ev.data);
                if (data.type === "message" && data.message) {
                    const cur = activeRef.current;
                    if (cur && cur.id === data.message.conversation_id) {
                        setMessages((prev) => {
                            if (prev.some((m) => m.id === data.message.id)) return prev;
                            return [...prev, data.message];
                        });
                        setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 100);
                    }
                    if (data.message.direction === "inbound") {
                        toast.message(lang === "de" ? "Neue Nachricht eingegangen" : "New message received", {
                            description: data.message.content_text?.slice(0, 80),
                        });
                    }
                } else if (data.type === "assignment") {
                    setAssignments((prev) => {
                        const next = { ...prev };
                        if (data.assignment) next[data.conversation_id] = data.assignment;
                        else delete next[data.conversation_id];
                        return next;
                    });
                }
            } catch (e) { /* ignore */ }
        };
        return () => { try { ws.close(); } catch (e) {} };
    }, [lang]);

    const loadConvs = useCallback(async (next = false, chFilter = null, stFilter = null) => {
        setLoading(true);
        try {
            const effective = chFilter !== null ? chFilter : channelFilter;
            const effectiveStatus = stFilter !== null ? stFilter : statusFilter;
            const params = new URLSearchParams();
            params.set("limit", "100");
            if (next && cursor) params.set("cursor", cursor);
            // If a specific channel is selected, find it and filter server-side
            if (effective && effective !== "all") {
                const ch = channels.find((c) => c.id === effective);
                if (ch?.id) params.set("channel_id", ch.id);
            }
            if (effectiveStatus && effectiveStatus !== "all") {
                params.set("status", effectiveStatus);
            }
            const { data } = await api.get(`/superchat/conversations?${params.toString()}`);
            const list = data?.data?.results || [];
            const newCursor = data?.data?.pagination?.next_cursor || data?.data?.pagination?.cursor || null;
            setConvs(next ? [...convs, ...list] : list);
            setCursor(newCursor);
            setHasMore(!!newCursor);
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        } finally { setLoading(false); }
    }, [cursor, convs, channelFilter, channels, statusFilter]);

    useEffect(() => { loadConvs(false); /* eslint-disable-next-line */ }, []);

    // Silent background refresh when AppLayout polls (every 5s)
    useEffect(() => {
        const h = () => loadConvs(false);
        window.addEventListener("mytacs:tick", h);
        return () => window.removeEventListener("mytacs:tick", h);
        // eslint-disable-next-line
    }, [channelFilter, statusFilter, channels]);
    // Reload when user switches channel or status filter (server-side filter)
    useEffect(() => {
        if (channels.length === 0 && channelFilter === "all") return;
        setCursor(null);
        loadConvs(false, channelFilter, statusFilter);
        // eslint-disable-next-line
    }, [channelFilter, statusFilter]);

    const loadMessages = useCallback(async (conv) => {
        setActive(conv);
        setMessages([]);
        setPlanNotice(null);
        try {
            // 1. Load locally captured messages (via webhook)
            const localRes = await api.get(`/superchat/messages/local/${conv.id}?limit=200`).catch(() => ({ data: { results: [] } }));
            const localList = localRes?.data?.results || [];
            if (localList.length > 0) {
                setMessages(localList);
                setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 100);
                return;
            }
            // 2. Fallback to Superchat API (will return notice if not supported)
            const { data } = await api.get(`/superchat/conversations/${conv.id}/messages?limit=50`);
            const list = data?.data?.results || data?.data?.data || [];
            setMessages(Array.isArray(list) ? list : []);
            if (data?.plan_limited || data?.no_api_history) setPlanNotice(data.notice || "");
            setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 100);
        } catch (e) { toast.error(errMsg(e, "Error")); }
    }, []);

    const sendReply = async () => {
        if (!reply.trim() || !active) return;
        setSending(true);
        try {
            const channel_id = active.inbox?.channel_id || active.channel?.id;
            const contact_id = (active.contacts || [])[0]?.id;
            await api.post(`/superchat/conversations/${active.id}/reply`, { text: reply, channel_id, contact_id });
            setReply("");
            toast.success("Gesendet");
            // Optimistically move active conversation to top of the list
            setConvs((prev) => {
                const idx = prev.findIndex((c) => c.id === active.id);
                if (idx <= 0) return prev;
                const next = [...prev];
                const [it] = next.splice(idx, 1);
                return [it, ...next];
            });
            setTimeout(() => loadMessages(active), 600);
            // Re-fetch conversations after a beat so Superchat's sort is reflected
            setTimeout(() => loadConvs(false), 1500);
        } catch (e) { toast.error(errMsg(e, "Send failed")); } finally { setSending(false); }
    };

    const changeStatus = async (newStatus, snoozedUntil = null) => {
        if (!active) return;
        try {
            const body = { status: newStatus };
            if (newStatus === "snoozed" && snoozedUntil) body.snoozed_until = snoozedUntil;
            await api.patch(`/superchat/conversations/${active.id}/status`, body);
            const labelMap = { open: "Offen", snoozed: "Später", done: "Erledigt" };
            toast.success(`${lang === "de" ? "Verschoben nach" : "Moved to"} ${labelMap[newStatus] || newStatus}`);
            // Optimistically remove from current list (since user is on a different status tab)
            if (newStatus !== statusFilter) {
                setConvs((prev) => prev.filter((c) => c.id !== active.id));
                setActive(null);
                setMessages([]);
            } else {
                // Refresh metadata of the conv in the list
                setConvs((prev) => prev.map((c) => c.id === active.id ? { ...c, status: newStatus, snoozed_until: snoozedUntil || null } : c));
            }
        } catch (e) { toast.error(errMsg(e, "Status-Update fehlgeschlagen")); }
    };

    // Load deletion request when active conversation changes
    useEffect(() => {
        if (!active?.id) { setDeletionRequest(null); return; }
        api.get(`/admin/deletion-requests`).then((r) => {
            const found = (r.data?.results || []).find((x) => x.entity_type === "superchat_conv" && x.entity_id === active.id);
            setDeletionRequest(found || null);
        }).catch(() => setDeletionRequest(null));
    }, [active?.id]);

    const isAdmin = currentUser?.role === "admin" || currentUser?.role === "master";

    const requestDeletion = async () => {
        if (!active) return;
        try {
            await api.post(`/superchat/conversations/${active.id}/request-deletion`, { reason: deleteReason });
            toast.success(lang === "de" ? "Löschantrag gesendet – wartet auf Genehmigung" : "Deletion requested – pending approval");
            setDeleteReasonOpen(false);
            setDeleteReason("");
            // Trigger reload of deletion request banner
            const r = await api.get(`/admin/deletion-requests`).catch(() => ({ data: { results: [] } }));
            const found = (r.data?.results || []).find((x) => x.entity_type === "superchat_conv" && x.entity_id === active.id);
            setDeletionRequest(found || null);
        } catch (e) { toast.error(errMsg(e, "Löschantrag fehlgeschlagen")); }
    };

    const cancelDeletionRequest = async () => {
        if (!active) return;
        try {
            await api.post(`/superchat/conversations/${active.id}/cancel-deletion-request`);
            toast.success(lang === "de" ? "Löschantrag zurückgezogen" : "Request cancelled");
            setDeletionRequest(null);
        } catch (e) { toast.error(errMsg(e, "Error")); }
    };

    const approveAndDelete = async () => {
        if (!active) return;
        const confirmMsg = lang === "de" ? "Konversation endgültig löschen?" : "Permanently delete this conversation?";
        if (!window.confirm(confirmMsg)) return;
        try {
            await api.delete(`/superchat/conversations/${active.id}`);
            toast.success(lang === "de" ? "Gelöscht" : "Deleted");
            setConvs((prev) => prev.filter((c) => c.id !== active.id));
            setActive(null);
            setMessages([]);
            setDeletionRequest(null);
        } catch (e) { toast.error(errMsg(e, "Löschen fehlgeschlagen")); }
    };

    const downloadExport = async () => {
        if (!active || exporting) return;
        setExporting(true);
        const tid = toast.loading(lang === "de" ? "Export wird vorbereitet… (kann ~30s dauern)" : "Preparing export… (~30s)");
        try {
            const { data: created } = await api.post(`/superchat/conversations/${active.id}/export`, {});
            const exportId = created?.data?.id || created?.id;
            if (!exportId) throw new Error("Keine Export-ID erhalten");
            // Poll up to 90s (15 tries x 6s)
            for (let i = 0; i < 15; i++) {
                await new Promise((r) => setTimeout(r, 6000));
                const { data: st } = await api.get(`/superchat/conversations/${active.id}/export/${exportId}`);
                const body = st?.data || st;
                const status = body?.status;
                if (status === "done" || status === "ready" || status === "completed") {
                    const url = body?.link?.url || body?.link;
                    if (url) {
                        toast.dismiss(tid);
                        toast.success(lang === "de" ? "Export bereit — Download startet" : "Ready — downloading");
                        window.open(url, "_blank");
                        return;
                    }
                }
                if (status === "failed" || status === "error") {
                    throw new Error(lang === "de" ? "Export fehlgeschlagen" : "Export failed");
                }
            }
            throw new Error(lang === "de" ? "Zeitüberschreitung beim Export" : "Export timeout");
        } catch (e) {
            toast.dismiss(tid);
            toast.error(errMsg(e, "Export error"));
        } finally { setExporting(false); }
    };

    // Server now does the channel filtering — convs only contains the selected channel's items.
    // (When "all" is selected, convs holds the most recent 100 across all channels.)
    const filteredConvs = convs;

    return (
        <div className="flex flex-col h-[calc(100vh-64px)]" data-testid="superchat-page">
            {!webhookMeta?.webhook_id && (
                <SuperchatWebhookSetup onRegistered={(meta) => setWebhookMeta(meta)} lang={lang} />
            )}
            <div className="flex flex-1 min-h-0">
                {/* Postfächer Sidebar — hidden on mobile (use channel dropdown / all-filter) */}
                <aside className="hidden md:flex w-56 border-r border-neutral-200 bg-white flex-col">
                <div className="p-3 border-b border-neutral-200">
                    <h2 className="font-display text-sm font-bold flex items-center gap-1.5 mb-2">
                        <Inbox className="w-4 h-4" /> {lang === "de" ? "Postfächer" : "Inboxes"}
                    </h2>
                </div>
                <div className="flex-1 overflow-y-auto">
                    <button onClick={() => setChannelFilter("all")} data-testid="sc-filter-all"
                        className={`w-full text-left px-3 py-2 text-xs border-b border-neutral-100 flex items-center gap-2 ${channelFilter === "all" ? "bg-blue-50 text-blue-900 font-semibold" : "hover:bg-neutral-50"}`}>
                        <Inbox className="w-4 h-4" />
                        <span>{lang === "de" ? "Alle Konversationen" : "All conversations"}{channelFilter === "all" ? ` (${convs.length})` : ""}</span>
                    </button>
                    {channels.map((ch) => {
                        // Only show a count when "all" is selected — otherwise the count is misleading
                        // because server-side filtering means `convs` only holds the active channel's items.
                        const count = channelFilter === "all"
                            ? convs.filter((c) => (c.inbox?.channel_id || c.channel?.id) === ch.id).length
                            : null;
                        const active = channelFilter === ch.id;
                        return (
                            <button key={ch.id} onClick={() => setChannelFilter(ch.id)} data-testid={`sc-filter-${ch.id}`}
                                className={`w-full text-left px-3 py-2 text-xs border-b border-neutral-100 flex items-start gap-2 ${active ? "bg-blue-50 text-blue-900 font-semibold" : "hover:bg-neutral-50"}`}>
                                <ChannelIcon type={ch.type} size={20} />
                                <div className="flex-1 min-w-0">
                                    <p className="truncate font-medium">{ch.name}</p>
                                    <p className="text-[10px] opacity-60">{channelLabel(ch.type)}{count !== null ? ` · ${count}` : ""}</p>
                                </div>
                            </button>
                        );
                    })}
                    {channels.length === 0 && (
                        <p className="p-4 text-xs text-neutral-500 italic">{lang === "de" ? "Keine Kanäle zugeordnet. Bitte Admin kontaktieren." : "No channels assigned."}</p>
                    )}
                </div>
            </aside>

            {/* Conversations List — hidden on mobile when a conversation is active */}
            <div className={`${active ? "hidden md:flex" : "flex"} w-full md:w-80 border-r border-neutral-200 bg-white flex-col`}>
                {/* Status Folders */}
                <div className="px-2 pt-2 pb-1 border-b border-neutral-200 flex gap-1">
                    {[
                        { key: "open", labelDe: "Offen", labelEn: "Open", icon: <Inbox className="w-3.5 h-3.5" /> },
                        { key: "snoozed", labelDe: "Später", labelEn: "Snoozed", icon: <Clock className="w-3.5 h-3.5" /> },
                        { key: "done", labelDe: "Erledigt", labelEn: "Done", icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
                    ].map((t) => {
                        const isOn = statusFilter === t.key;
                        return (
                            <button
                                key={t.key}
                                onClick={() => setStatusFilter(t.key)}
                                data-testid={`sc-status-${t.key}`}
                                className={`flex-1 flex items-center justify-center gap-1 text-[11px] font-semibold px-2 py-1.5 rounded-md transition-colors ${
                                    isOn
                                        ? "bg-neutral-900 text-white"
                                        : "text-neutral-600 hover:bg-neutral-100"
                                }`}
                            >
                                {t.icon}
                                <span>{lang === "de" ? t.labelDe : t.labelEn}</span>
                            </button>
                        );
                    })}
                </div>
                <div className="px-3 py-2.5 border-b border-neutral-200 flex items-center justify-between gap-1">
                    <h3 className="text-xs font-semibold uppercase tracking-wider">{lang === "de" ? "Konversationen" : "Conversations"} · {filteredConvs.length}</h3>
                    <div className="flex items-center gap-0.5">
                        <Button onClick={() => setNewConvOpen(true)} variant="ghost" size="sm" className="h-7 px-2" data-testid="sc-new-conv-btn" title={lang === "de" ? "Neue Konversation" : "New conversation"}><Plus className="w-3.5 h-3.5" /></Button>
                        <Button onClick={() => loadConvs(false)} variant="ghost" size="sm" className="h-7 px-2" data-testid="sc-refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                    {loading && filteredConvs.length === 0 ? (
                        <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-neutral-400" /></div>
                    ) : filteredConvs.length === 0 ? (
                        <p className="p-6 text-center text-xs text-neutral-500">{lang === "de" ? "Keine Konversationen" : "No conversations"}</p>
                    ) : filteredConvs.map((c) => {
                        const contact = (c.contacts || [])[0] || {};
                        const isActive = active?.id === c.id;
                        return (
                            <button key={c.id} onClick={() => loadMessages(c)} data-testid={`sc-conv-${c.id}`}
                                className={`w-full flex items-start gap-2.5 p-3 border-b border-neutral-100 text-left ${isActive ? "bg-blue-50" : "hover:bg-neutral-50"}`}>
                                <ChannelIcon type={c.channel?.type} size={36} />
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-sm truncate">{contact.name || contact.first_name || contact.id?.slice(0, 12) || "—"}</p>
                                    {contact.handle_value && (
                                        <p className="text-[10px] text-neutral-500 truncate font-mono">{contact.handle_value}</p>
                                    )}
                                    <p className="text-[10px] text-neutral-500 truncate">{c.inbox?.name} · {channelLabel(c.channel?.type)}</p>
                                    {c.status === "snoozed" && c.snoozed_until && (
                                        <p className="text-[10px] text-amber-700 font-semibold mt-0.5 flex items-center gap-1">
                                            <Clock className="w-2.5 h-2.5" /> {new Date(c.snoozed_until).toLocaleString("de-DE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                                        </p>
                                    )}
                                    {assignments[c.id] && (
                                        <p className="text-[10px] text-blue-700 font-semibold mt-0.5 flex items-center gap-1" data-testid={`sc-conv-assigned-${c.id}`}>
                                            <UserCog className="w-2.5 h-2.5" /> {assignments[c.id].assigned_user_name}
                                        </p>
                                    )}
                                </div>
                                {c.unread_count > 0 && (
                                    <span className="bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{c.unread_count}</span>
                                )}
                            </button>
                        );
                    })}
                    {hasMore && (
                        <button onClick={() => loadConvs(true)} disabled={loading} data-testid="sc-load-more" className="w-full p-2 text-xs text-blue-700 hover:bg-blue-50 border-t">
                            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : (lang === "de" ? "Mehr laden" : "Load more")}
                        </button>
                    )}
                </div>
            </div>

            {/* Chat Window — hidden on mobile when no active conversation */}
            <main className={`${active ? "flex" : "hidden md:flex"} flex-1 flex-col bg-neutral-50 min-w-0`}>
                {!active ? (
                    <div className="flex-1 flex items-center justify-center text-neutral-400">
                        <div className="text-center">
                            <MessageCircle className="w-16 h-16 mx-auto opacity-30 mb-2" />
                            <p className="text-sm">{lang === "de" ? "Wähle eine Konversation" : "Pick a conversation"}</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <header className="px-3 py-2 sm:px-4 sm:py-3 border-b border-neutral-200 bg-white">
                            <div className="flex items-center gap-2 flex-wrap">
                                <button
                                    onClick={() => setActive(null)}
                                    className="md:hidden p-1 -ml-1 rounded hover:bg-neutral-100"
                                    data-testid="sc-back-btn"
                                    aria-label="Zurück"
                                >
                                    <ChevronLeft className="w-5 h-5 text-neutral-700" />
                                </button>
                                {((active.contacts || [])[0]) && (
                                    <div className="w-9 h-9 rounded-full bg-neutral-900 text-white font-semibold text-xs flex items-center justify-center">
                                        {((active.contacts || [])[0]?.name || "?").slice(0, 2).toUpperCase()}
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <button onClick={() => setContactEditFor((active.contacts || [])[0])} className="text-left hover:underline decoration-dotted" data-testid="sc-edit-contact">
                                        <p className="font-semibold">{((active.contacts || [])[0]?.name) || "—"}</p>
                                        {((active.contacts || [])[0]?.handle_value) && (
                                            <p className="text-[10px] text-neutral-500 font-mono">{(active.contacts || [])[0]?.handle_value}</p>
                                        )}
                                    </button>
                                    <p className="text-[10px] text-neutral-500">{active.inbox?.name} · {channelLabel(active.channel?.type)}</p>
                                </div>
                                <ConvAssignmentBadge
                                    convId={active.id}
                                    channelId={active.inbox?.channel_id || active.channel?.id}
                                    assignment={assignments[active.id]}
                                    users={users}
                                    onChange={(a) => setAssignments((prev) => { const n = { ...prev }; if (a) n[active.id] = a; else delete n[active.id]; return n; })}
                                    lang={lang}
                                />
                                {webhookMeta?.webhook_id && (
                                    <span className={`text-[10px] font-semibold flex items-center gap-1 px-2 py-1 rounded-full ${wsLive ? "bg-emerald-100 text-emerald-800" : "bg-neutral-100 text-neutral-600"}`} data-testid="sc-live-indicator" title={wsLive ? "Live verbunden" : "Verbindung wird hergestellt..."}>
                                        {wsLive ? <Zap className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                                        {wsLive ? (lang === "de" ? "Live" : "Live") : (lang === "de" ? "Offline" : "Offline")}
                                    </span>
                                )}
                                <Button onClick={() => setTemplatePickerOpen(true)} variant="outline" size="sm" className="h-8 gap-1.5" data-testid="sc-tpl-btn" title={lang === "de" ? "Vorlage senden (für >24h-Fenster)" : "Send template"}>
                                    <FileText className="w-3.5 h-3.5" />
                                    <span className="text-xs">{lang === "de" ? "Vorlage" : "Template"}</span>
                                </Button>
                                <Button onClick={downloadExport} disabled={exporting} variant="outline" size="sm" className="h-8 gap-1.5" data-testid="sc-download-btn">
                                    {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                                    <span className="text-xs">{lang === "de" ? "PDF" : "PDF"}</span>
                                </Button>
                                {/* Status actions: depend on the current conversation status */}
                                {active.status !== "done" && (
                                    <Button onClick={() => changeStatus("done")} variant="outline" size="sm" className="h-8 gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="sc-done-btn" title={lang === "de" ? "Als erledigt markieren" : "Mark as done"}>
                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                        <span className="text-xs">{lang === "de" ? "Erledigt" : "Done"}</span>
                                    </Button>
                                )}
                                {active.status !== "snoozed" && active.status !== "done" && (
                                    <Button onClick={() => setSnoozeOpen(true)} variant="outline" size="sm" className="h-8 gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="sc-snooze-btn" title={lang === "de" ? "Später erinnern" : "Snooze"}>
                                        <Clock className="w-3.5 h-3.5" />
                                        <span className="text-xs">{lang === "de" ? "Später" : "Snooze"}</span>
                                    </Button>
                                )}
                                {(active.status === "done" || active.status === "snoozed") && (
                                    <Button onClick={() => changeStatus("open")} variant="outline" size="sm" className="h-8 gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="sc-reopen-btn" title={lang === "de" ? "Wieder öffnen" : "Reopen"}>
                                        <Undo2 className="w-3.5 h-3.5" />
                                        <span className="text-xs">{lang === "de" ? "Öffnen" : "Open"}</span>
                                    </Button>
                                )}
                                {/* Deletion: admins delete directly; others must request approval */}
                                {!deletionRequest && (
                                    isAdmin ? (
                                        <Button onClick={approveAndDelete} variant="outline" size="sm" className="h-8 gap-1.5 border-red-300 text-red-700 hover:bg-red-50" data-testid="sc-delete-btn" title={lang === "de" ? "Konversation löschen (Admin)" : "Delete conversation (admin)"}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                            <span className="text-xs">{lang === "de" ? "Löschen" : "Delete"}</span>
                                        </Button>
                                    ) : (
                                        <Button onClick={() => setDeleteReasonOpen(true)} variant="outline" size="sm" className="h-8 gap-1.5 border-red-300 text-red-700 hover:bg-red-50" data-testid="sc-request-delete-btn" title={lang === "de" ? "Löschung beantragen" : "Request deletion"}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                            <span className="text-xs">{lang === "de" ? "Löschantrag" : "Request delete"}</span>
                                        </Button>
                                    )
                                )}
                            </div>
                            {/* Pending deletion request banner */}
                            {deletionRequest && (
                                <div className="mt-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 flex items-start justify-between gap-3" data-testid="sc-deletion-banner">
                                    <div className="flex items-start gap-2 min-w-0 flex-1">
                                        <AlertTriangle className="w-4 h-4 text-red-700 mt-0.5 shrink-0" />
                                        <div className="min-w-0">
                                            <p className="text-xs font-semibold text-red-900">{lang === "de" ? "Löschantrag offen" : "Deletion requested"}</p>
                                            <p className="text-[11px] text-red-800">
                                                <strong>{deletionRequest.requested_by_name}</strong>{" · "}{new Date(deletionRequest.requested_at).toLocaleString(LOCALES[lang])}
                                                {deletionRequest.reason && <> · <em>&ldquo;{deletionRequest.reason}&rdquo;</em></>}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex gap-1.5 shrink-0">
                                        {isAdmin ? (
                                            <>
                                                <Button size="sm" variant="outline" onClick={cancelDeletionRequest} className="h-7 px-2" data-testid="sc-deletion-reject"><XIcon className="w-3.5 h-3.5" /></Button>
                                                <Button size="sm" onClick={approveAndDelete} className="h-7 px-2 bg-red-600 hover:bg-red-700 text-white" data-testid="sc-deletion-approve"><Trash2 className="w-3.5 h-3.5 mr-1" />{lang === "de" ? "Genehmigen" : "Approve"}</Button>
                                            </>
                                        ) : (
                                            deletionRequest.requested_by === currentUser?.id && (
                                                <Button size="sm" variant="outline" onClick={cancelDeletionRequest} className="h-7" data-testid="sc-deletion-cancel">{lang === "de" ? "Zurückziehen" : "Cancel"}</Button>
                                            )
                                        )}
                                    </div>
                                </div>
                            )}
                        </header>
                        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="sc-messages">
                            {planNotice && (
                                <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 flex items-start gap-2" data-testid="sc-plan-notice">
                                    <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                                    <div className="text-xs text-amber-900 leading-relaxed">
                                        <p className="font-semibold mb-0.5">{lang === "de" ? "Verlauf nur als PDF-Download verfügbar" : "History only via PDF export"}</p>
                                        <p>{lang === "de" ? "Die Superchat-API bietet keinen Live-Verlauf in JSON. Klick oben rechts auf \u201EVerlauf als PDF\u201C \u2014 du bekommst den kompletten Chat inkl. aller Bilder/Anh\u00E4nge als ZIP heruntergeladen. Antworten/Senden unten funktioniert weiterhin." : "Superchat API offers no JSON history. Use \u201CDownload history\u201D top-right to get the full chat as ZIP. Replies below work as usual."}</p>
                                    </div>
                                </div>
                            )}
                            {messages.length === 0 ? (
                                <p className="text-center text-xs text-neutral-400 py-8">{planNotice ? (lang === "de" ? "Verlauf gesperrt — Antworten funktioniert weiterhin unten." : "History locked — replies still work below.") : (lang === "de" ? "Keine Nachrichten" : "No messages")}</p>
                            ) : messages.map((m) => {
                                // Support both old API shape and new webhook-normalized shape
                                const outgoing = m.direction === "outbound" || m.direction === "outgoing" || m.type === "outgoing";
                                const text = m.content_text || m.content?.text?.text || m.text || m.content?.email?.text || m.content?.body || "";
                                const isTemplate = m.content_type === "whats_app_template";
                                return (
                                    <div key={m.id} className={`flex ${outgoing ? "justify-end" : "justify-start"}`} data-testid={`sc-bubble-${m.id}`}>
                                        <div className={`max-w-[70%] ${outgoing ? "bg-blue-600 text-white" : "bg-white border border-neutral-200"} rounded-2xl px-3.5 py-2`}>
                                            {outgoing && m.sent_by_user_name && (
                                                <p className="text-[10px] font-semibold text-blue-100 mb-0.5 flex items-center gap-1">
                                                    {m.sent_by_user_name}
                                                    {isTemplate && <span className="text-[9px] bg-blue-700 px-1 py-px rounded">VORLAGE</span>}
                                                </p>
                                            )}
                                            <p className="text-sm whitespace-pre-wrap break-words">{text || (isTemplate ? "[Template]" : "[ohne Text]")}</p>
                                            <p className={`text-[10px] mt-0.5 ${outgoing ? "text-blue-200" : "text-neutral-400"}`}>
                                                {m.created_at && new Date(m.created_at).toLocaleString(LOCALES[lang])}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <footer className="p-3 bg-white border-t border-neutral-200 flex items-end gap-2">
                            <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder={lang === "de" ? "Antwort schreiben..." : "Type a reply..."} rows={2} data-testid="sc-reply" className="flex-1 resize-none" />
                            <Button onClick={sendReply} disabled={sending || !reply.trim()} data-testid="sc-send-btn" className="bg-brand-500 hover:bg-brand-600 h-auto"><Send className="w-4 h-4" /></Button>
                        </footer>
                    </>
                )}
            </main>
            </div>

            <TemplatePickerDialog
                open={templatePickerOpen}
                onClose={() => setTemplatePickerOpen(false)}
                channelId={active?.inbox?.channel_id || active?.channel?.id}
                contactId={(active?.contacts || [])[0]?.id}
                identifier={(active?.contacts || [])[0]?.handle_value}
                onSent={() => { setTemplatePickerOpen(false); if (active) setTimeout(() => loadMessages(active), 1500); }}
                lang={lang}
            />
            <NewConversationDialog
                open={newConvOpen}
                onClose={() => setNewConvOpen(false)}
                channels={channels}
                onSent={() => { setNewConvOpen(false); setTimeout(() => loadConvs(false), 1500); }}
                lang={lang}
            />
            <EditContactDialog
                contact={contactEditFor}
                onClose={() => setContactEditFor(null)}
                onSaved={() => { setContactEditFor(null); if (active) loadConvs(false); }}
                lang={lang}
            />
            <SnoozeDialog
                open={snoozeOpen}
                onClose={() => setSnoozeOpen(false)}
                onConfirm={(iso) => { setSnoozeOpen(false); changeStatus("snoozed", iso); }}
                lang={lang}
            />
            <Dialog open={deleteReasonOpen} onOpenChange={(o) => { if (!o) setDeleteReasonOpen(false); }}>
                <DialogContent data-testid="sc-delete-reason-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><Trash2 className="w-4 h-4 text-red-600" /> {lang === "de" ? "Löschantrag stellen" : "Request deletion"}</DialogTitle>
                        <DialogDescription>{lang === "de" ? "Ein Admin muss die Löschung genehmigen. Bitte gib einen Grund an (optional)." : "An admin must approve the deletion. You can optionally add a reason."}</DialogDescription>
                    </DialogHeader>
                    <Textarea
                        value={deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                        placeholder={lang === "de" ? "Grund (optional)" : "Reason (optional)"}
                        rows={3}
                        data-testid="sc-delete-reason-input"
                    />
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => { setDeleteReasonOpen(false); setDeleteReason(""); }} data-testid="sc-delete-reason-cancel">{lang === "de" ? "Abbrechen" : "Cancel"}</Button>
                        <Button onClick={requestDeletion} className="bg-red-600 hover:bg-red-700 text-white" data-testid="sc-delete-reason-submit">{lang === "de" ? "Antrag senden" : "Submit request"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function ConvAssignmentBadge({ convId, channelId, assignment, users, onChange, lang }) {
    const { user: me } = useAuth();
    const [open, setOpen] = useState(false);
    const isPriv = me?.role === "admin" || me?.role === "master";
    // Eligible users: admin/master always OK; others must have channelId in superchat_channel_ids
    const eligible = (users || []).filter((u) => {
        if (u.role === "admin" || u.role === "master") return true;
        if (!channelId) return false;
        return (u.superchat_channel_ids || []).includes(channelId);
    });

    const assign = async (uid) => {
        try {
            if (!uid) {
                await api.delete(`/superchat/conversations/${convId}/assign`);
                onChange(null);
                toast.success(lang === "de" ? "Zuweisung entfernt" : "Unassigned");
            } else {
                const { data } = await api.post(`/superchat/conversations/${convId}/assign`, { user_id: uid, channel_id: channelId });
                onChange(data);
                toast.success(lang === "de" ? "Zugewiesen" : "Assigned");
            }
        } catch (e) { toast.error(errMsg(e, "Error")); }
        setOpen(false);
    };

    if (!isPriv && !assignment) return null;
    if (!isPriv) {
        return (
            <span className="text-[10px] font-semibold flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100 text-blue-800" title={assignment.assigned_user_name}>
                <UserCog className="w-3 h-3" /> {assignment.assigned_user_name}
            </span>
        );
    }

    return (
        <div className="relative">
            <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs"
                onClick={() => setOpen(!open)}
                data-testid="sc-assign-btn"
            >
                <UserCog className="w-3.5 h-3.5" />
                {assignment ? assignment.assigned_user_name : (lang === "de" ? "Zuweisen" : "Assign")}
            </Button>
            {open && (
                <div className="absolute right-0 top-9 z-50 bg-white border border-neutral-200 rounded-lg shadow-lg w-56 py-1 max-h-72 overflow-y-auto" data-testid="sc-assign-menu">
                    <button onClick={() => assign(null)} className="w-full text-left px-3 py-1.5 text-xs hover:bg-neutral-100 italic text-neutral-600">
                        {lang === "de" ? "— Niemand —" : "— Nobody —"}
                    </button>
                    {eligible.length === 0 ? (
                        <p className="px-3 py-2 text-[10px] text-neutral-500 italic">{lang === "de" ? "Niemand hat Berechtigung für diesen Kanal" : "Nobody has access to this channel"}</p>
                    ) : eligible.map((u) => {
                        const active = assignment?.assigned_user_id === u.id;
                        return (
                            <button key={u.id} onClick={() => assign(u.id)} data-testid={`sc-assign-${u.id}`}
                                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-neutral-100 flex items-center justify-between ${active ? "bg-blue-50 font-semibold" : ""}`}>
                                <span>{u.name}</span>
                                <span className="text-[9px] text-neutral-500 uppercase">{u.role}</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function TemplatePickerDialog({ open, onClose, channelId, contactId, identifier, onSent, lang }) {
    const [templates, setTemplates] = useState([]);
    const [selectedId, setSelectedId] = useState("");
    const [variables, setVariables] = useState({});
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !channelId) return;
        setLoading(true);
        api.get(`/superchat/templates?channel_id=${encodeURIComponent(channelId)}`)
            .then((r) => setTemplates(r.data?.data?.results || []))
            .catch((e) => toast.error(errMsg(e, "Templates fehlgeschlagen")))
            .finally(() => setLoading(false));
        setSelectedId(""); setVariables({});
    }, [open, channelId]);

    const selectedTemplate = templates.find((t) => t.id === selectedId);
    const tplBody = selectedTemplate?.content?.body || "";
    const tplVars = selectedTemplate?.content?.variables || [];
    // Preview with substitutions
    const preview = tplBody.replace(/\{\{(\d+)\}\}/g, (_, pos) => variables[pos] || `[Variable ${pos}]`);

    const send = async () => {
        if (!selectedId) return;
        setSending(true);
        try {
            await api.post("/superchat/templates/send", {
                template_id: selectedId,
                contact_id: contactId,
                identifier,
                channel_id: channelId,
                variables,
            });
            toast.success(lang === "de" ? "Vorlage gesendet" : "Template sent");
            onSent();
        } catch (e) { toast.error(errMsg(e, "Send failed")); } finally { setSending(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid="sc-template-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">{lang === "de" ? "WhatsApp-Vorlage senden" : "Send WhatsApp template"}</DialogTitle>
                    <DialogDescription className="text-xs">{lang === "de" ? "Außerhalb des 24h-Antwort-Fensters dürfen laut Meta nur genehmigte Vorlagen versendet werden." : "Outside the 24h window, only approved templates may be sent (Meta rule)."}</DialogDescription>
                </DialogHeader>
                {loading ? (
                    <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
                ) : (
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">{lang === "de" ? "Vorlage" : "Template"} ({templates.length})</Label>
                            <Select value={selectedId} onValueChange={(v) => { setSelectedId(v); setVariables({}); }}>
                                <SelectTrigger className="mt-1.5" data-testid="sc-tpl-select"><SelectValue placeholder={lang === "de" ? "Vorlage wählen…" : "Choose template…"} /></SelectTrigger>
                                <SelectContent>
                                    {templates.map((t) => (
                                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {selectedTemplate && (
                            <>
                                {tplVars.map((v) => (
                                    <div key={v.position}>
                                        <Label className="text-xs">{lang === "de" ? `Variable ${v.position}` : `Variable ${v.position}`}: <span className="font-normal text-neutral-500">{v.display_name}</span></Label>
                                        <Input
                                            value={variables[String(v.position)] || ""}
                                            onChange={(e) => setVariables((p) => ({ ...p, [String(v.position)]: e.target.value }))}
                                            placeholder={v.display_name}
                                            className="mt-1"
                                            data-testid={`sc-tpl-var-${v.position}`}
                                        />
                                    </div>
                                ))}
                                <div>
                                    <Label className="text-xs font-semibold uppercase tracking-wider">{lang === "de" ? "Vorschau" : "Preview"}</Label>
                                    <div className="mt-1 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm whitespace-pre-wrap break-words" data-testid="sc-tpl-preview">
                                        {preview}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{lang === "de" ? "Abbrechen" : "Cancel"}</Button>
                    <Button onClick={send} disabled={!selectedId || sending} className="bg-brand-500 hover:bg-brand-600" data-testid="sc-tpl-send">
                        {sending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                        {lang === "de" ? "Vorlage senden" : "Send template"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function NewConversationDialog({ open, onClose, channels, onSent, lang }) {
    const [channelId, setChannelId] = useState("");
    const [identifier, setIdentifier] = useState("");
    const [content, setContent] = useState("");
    const [templates, setTemplates] = useState([]);
    const [useTemplate, setUseTemplate] = useState(false);
    const [tplId, setTplId] = useState("");
    const [tplVars, setTplVars] = useState({});
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!open) return;
        setChannelId(""); setIdentifier(""); setContent(""); setUseTemplate(false); setTplId(""); setTplVars({});
        setTemplates([]);
    }, [open]);

    useEffect(() => {
        if (channelId) {
            api.get(`/superchat/templates?channel_id=${encodeURIComponent(channelId)}`)
                .then((r) => setTemplates(r.data?.data?.results || []))
                .catch(() => setTemplates([]));
        }
    }, [channelId]);

    const selectedTpl = templates.find((t) => t.id === tplId);
    const tplVarsList = selectedTpl?.content?.variables || [];
    const selectedChannel = channels.find((c) => c.id === channelId);
    const isWhatsApp = (selectedChannel?.type || "").toLowerCase().includes("whats");

    const send = async () => {
        if (!channelId || !identifier.trim()) {
            toast.error(lang === "de" ? "Kanal und Empfänger erforderlich" : "Channel and recipient required");
            return;
        }
        setSending(true);
        try {
            const body = { channel_id: channelId, identifier: identifier.trim() };
            if (useTemplate && tplId) {
                body.template_id = tplId;
                body.variables = tplVars;
            } else {
                body.content = content;
            }
            await api.post("/superchat/conversations/new", body);
            toast.success(lang === "de" ? "Nachricht gesendet" : "Message sent");
            onSent();
        } catch (e) { toast.error(errMsg(e, "Send failed")); } finally { setSending(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="sc-new-conv-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">{lang === "de" ? "Neue Konversation starten" : "Start new conversation"}</DialogTitle>
                    <DialogDescription className="text-xs">
                        {lang === "de" ? "WhatsApp-Neukontakte: Meta verlangt die Verwendung einer Vorlage." : "WhatsApp new contacts: Meta requires a template."}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{lang === "de" ? "Kanal" : "Channel"}</Label>
                        <Select value={channelId} onValueChange={setChannelId}>
                            <SelectTrigger className="mt-1.5" data-testid="sc-new-channel"><SelectValue placeholder={lang === "de" ? "Kanal wählen…" : "Choose channel…"} /></SelectTrigger>
                            <SelectContent>
                                {channels.map((c) => (
                                    <SelectItem key={c.id} value={c.id}>{c.name} ({c.type})</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">
                            {lang === "de" ? "Empfänger" : "Recipient"}
                        </Label>
                        <Input
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            placeholder={isWhatsApp ? "+491701234567" : (lang === "de" ? "Telefon oder Email" : "Phone or email")}
                            className="mt-1.5"
                            data-testid="sc-new-identifier"
                        />
                    </div>
                    {channelId && (
                        <div>
                            <label className="flex items-center gap-2 text-xs cursor-pointer">
                                <input type="checkbox" checked={useTemplate} onChange={(e) => setUseTemplate(e.target.checked)} data-testid="sc-new-use-template" />
                                <FileText className="w-3.5 h-3.5" />
                                {lang === "de" ? "Vorlage verwenden" : "Use template"} {isWhatsApp && <span className="text-amber-600 font-semibold">{lang === "de" ? "(empfohlen für WhatsApp-Neukontakte)" : "(recommended for WhatsApp new contacts)"}</span>}
                            </label>
                        </div>
                    )}
                    {useTemplate ? (
                        <>
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-wider">{lang === "de" ? "Vorlage" : "Template"} ({templates.length})</Label>
                                <Select value={tplId} onValueChange={(v) => { setTplId(v); setTplVars({}); }}>
                                    <SelectTrigger className="mt-1.5" data-testid="sc-new-tpl-select"><SelectValue placeholder={lang === "de" ? "Vorlage wählen" : "Choose template"} /></SelectTrigger>
                                    <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            {tplVarsList.map((v) => (
                                <div key={v.position}>
                                    <Label className="text-xs">{`Variable ${v.position}`}: <span className="font-normal text-neutral-500">{v.display_name}</span></Label>
                                    <Input value={tplVars[String(v.position)] || ""} onChange={(e) => setTplVars((p) => ({ ...p, [String(v.position)]: e.target.value }))} className="mt-1" />
                                </div>
                            ))}
                            {selectedTpl && (
                                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm whitespace-pre-wrap">
                                    {selectedTpl.content?.body?.replace(/\{\{(\d+)\}\}/g, (_, p) => tplVars[p] || `[${p}]`)}
                                </div>
                            )}
                        </>
                    ) : (
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">{lang === "de" ? "Nachricht" : "Message"}</Label>
                            <Textarea value={content} onChange={(e) => setContent(e.target.value)} rows={4} className="mt-1.5" placeholder={lang === "de" ? "Nachricht..." : "Message..."} data-testid="sc-new-content" />
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{lang === "de" ? "Abbrechen" : "Cancel"}</Button>
                    <Button onClick={send} disabled={sending || (!useTemplate && !content) || (useTemplate && !tplId)} className="bg-brand-500 hover:bg-brand-600" data-testid="sc-new-send">
                        {sending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                        {lang === "de" ? "Senden" : "Send"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function EditContactDialog({ contact, onClose, onSaved, lang }) {
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (contact) {
            setFirstName(contact.first_name || "");
            setLastName(contact.last_name || "");
        }
    }, [contact]);

    if (!contact?.id) return null;

    const save = async () => {
        setBusy(true);
        try {
            await api.patch(`/superchat/contacts/${contact.id}`, { first_name: firstName, last_name: lastName });
            toast.success(lang === "de" ? "Kontakt gespeichert" : "Contact saved");
            onSaved();
        } catch (e) { toast.error(errMsg(e, "Save failed")); } finally { setBusy(false); }
    };

    return (
        <Dialog open={!!contact?.id} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-sm" data-testid="sc-edit-contact-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2"><UserCog className="w-4 h-4" /> {lang === "de" ? "Kontakt bearbeiten" : "Edit contact"}</DialogTitle>
                    <DialogDescription className="text-xs font-mono">{contact.handle_value || contact.id}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">{lang === "de" ? "Vorname" : "First name"}</Label>
                        <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="mt-1" data-testid="sc-contact-first" />
                    </div>
                    <div>
                        <Label className="text-xs">{lang === "de" ? "Nachname" : "Last name"}</Label>
                        <Input value={lastName} onChange={(e) => setLastName(e.target.value)} className="mt-1" data-testid="sc-contact-last" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{lang === "de" ? "Abbrechen" : "Cancel"}</Button>
                    <Button onClick={save} disabled={busy} className="bg-neutral-900 hover:bg-neutral-800" data-testid="sc-contact-save">
                        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                        {lang === "de" ? "Speichern" : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function SuperchatWebhookSetup({ onRegistered, lang }) {
    const [busy, setBusy] = useState(false);
    const register = async () => {
        setBusy(true);
        try {
            const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
            const target = `${base.replace(/\/$/, "")}/api/superchat/webhook`;
            const { data } = await api.post("/superchat/webhook/register", {
                target_url: target,
                events: ["message_inbound", "message_outbound", "conversation_opened", "conversation_done"],
            });
            toast.success(lang === "de" ? "Webhook aktiv \u2014 neue Nachrichten erscheinen ab jetzt live" : "Webhook active \u2014 new messages will arrive live");
            // Reload status
            const st = await api.get("/superchat/webhook/status");
            onRegistered(st.data?.meta || data);
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        } finally { setBusy(false); }
    };
    return (
        <div className="px-4 py-2.5 bg-blue-50 border-b border-blue-200 flex items-center gap-3" data-testid="sc-webhook-setup">
            <Zap className="w-4 h-4 text-blue-700 shrink-0" />
            <div className="flex-1 text-xs">
                <p className="font-semibold text-blue-900">{lang === "de" ? "Live-Modus nicht aktiv" : "Live mode inactive"}</p>
                <p className="text-blue-800">{lang === "de" ? "Einmalig einrichten: Neue eingehende WhatsApp/SMS/Email-Nachrichten erscheinen dann automatisch hier in der UI \u2014 ohne Refresh." : "One-time setup: incoming WhatsApp/SMS/Email messages will appear here automatically \u2014 no refresh needed."}</p>
            </div>
            <Button onClick={register} disabled={busy} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="sc-webhook-register-btn">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
                {lang === "de" ? "Live-Modus aktivieren" : "Activate live mode"}
            </Button>
        </div>
    );
}
